import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import { useLazyListContractorQuery } from "store/services/contractorApi";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";
import TableFilter from "components/Shared/AppTable/TableFilter";

import { useAppMeta, useTablePagination } from "hooks";

const ContractorListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Contractor Profiles" }];

  const contractorListTabs = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inActive" },
    { label: "Expiring Soon", value: "Expiring Soon" },
  ];

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <Link
          title={row?.name}
          to={`/app/crm/contractor/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },

    {
      name: "Contractor ID",
      selector: (row) => row?.contractorId,
      cell: (row) => (
        <a
          href={`/app/crm/contractor/${row?._id}/detail`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.contractorId ? (
            <HighlightedText text={row?.contractorId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    {
      name: "Driver",
      selector: (row) => row?.driverId?.map((d) => d.name).join(", ") || "",
      cell: (row) => row?.driverId?.map((d) => d.name).join(", ") || "",
    },
    {
      name: "Vehicle ID",
      selector: (row) => row?.vehicle?.map((v) => v.vehicle_id).join(", "),
      cell: (row) => row?.vehicle?.map((v) => v.vehicle_id).join(", "),
    },

    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <a
          href={`mailto:${row?.email}`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.email ? (
            <HighlightedText text={row?.email} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      cell: (row) => {
        let phone = row?.phone || "";
        const lastDash = phone.lastIndexOf("-");
        if (lastDash !== -1) {
          phone = phone.substring(0, lastDash).trim();
        }
        return (
          <>
            {phone ? (
              <HighlightedText text={phone} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </>
        );
      },
      sortable: true,
    },
    { name: "Category", selector: (row) => row?.category },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/contractor/${row._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Contractor"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/contractor/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Contractor">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchContractors,
    { data: contractorRes, isLoading, isFetching, error },
  ] = useLazyListContractorQuery();

  React.useEffect(() => {
    fetchContractors({
      status: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchContractors, keyword, perPage]);

  useAppMeta(`${activeTab} Contractors`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={contractorListTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Name/Display Name/Email"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={contractorRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={contractorRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ContractorListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/contractor/create">
        <Button startIcon={<Add />} variant="contained">
          New Contractor Profile
        </Button>
      </Link>
    </>
  );
};
